import { Environment } from 'app/app/api/config'
import { OFFER_STATUS, sizes } from 'app/types/types'
import { NavigationTab } from 'app/types/ui/grouping'

const VALID_PAGE = ['prepare', 'pricing', 'grouping', 'offers', 'contract'].join('|')
const VALID_SUBPAGE = [
  'location',
  'elevator',
  'escalator',
  'account',
  'contract',
  'contact',
  'view-equipments',
  'edit',
  'overview'
].join('|')

export const BUILDING = 'location'
export const ELEVATOR = 'elevator'
export const ESCALATOR = 'escalator'

export const Constants = {
  MAX_EQUIPMENT_LIMIT: 400,
  MAX_TEXTAREA_CHAR_LIMIT: 2000,
  ONLINE: 'online',
  OFFLINE: 'offline',
  OFFLINE_LIMIT: 72,
  ADD: 'add',
  REMOVE: 'remove',
  MAX_TENDER_VERSION_COMMENT_LENGTH: 50,
  DEVELOPMENT: 'development',

  // common
  UNDEFINED: 'undefined',

  // dexie config
  FRONT_LINE_CONFIG: 'frontline',
  PROFILE: 'profile',
  SYNC_TIME_STAMP: 'syncTimestamp',
  SETTINGS: 'settings',

  // Header strings
  HEADER_NAME: 'KONE CARE CONTRACT',
  HEADER_PRE_PRODUCTION: 'Pre-production',
  HEADER_STORYBOOK: 'Storybook',
  HEADER_HOME_LINK: '/',
  HEADER_STORYBOOK_LINK: '/storybook',
  HEADER_PRE_PRODUCTION_LINK: '/pre-production',
  NINE_PLUS: '9+',

  // Debounce Delay for Search box
  DEBOUNCE_DELAY: 400,

  // Group Limits
  MAX_GROUPS_LIMIT: 6,

  // Group toast notification duration in seconds
  GROUP_TOAST_DURATION: 5,

  AMOUNT_INPUT_REGEX: /^\d*([.,]\d{0,2})?$/,
  DISCOUNT_INPUT_REGEX: /^-?\d*\.?\d*$/,
  DISCOUNT_DEFAULT_VALUE: '0',

  // Regex
  VALID_URL_REGEX: new RegExp(`^\\/$|^\\/$|^\\/[\\w\\d]+\\/(${VALID_PAGE})(\\/(${VALID_SUBPAGE}))?$`),

  // Image Link
  NO_RESULT_FOUND_IMAGE: '/images/customerPageImages/NoResults.svg',
  NO_DATA_IMAGE: '/images/customerPageImages/ImportOpportunities.svg',
  CONTRACT_SUCCESS_IMAGE: '/images/contractPageImages/contractSuccess.svg',
  APP_CONFIG_KEY: 'app-config',

  // Query Params
  QUERY_SHARE: 'share',

  // Tab values
  TAB_MY_OPPORTUNITIES: 'myOpportunities',
  TAB_SHARED_OPPORTUNITIES: 'sharedOpportunities'
}

export const progressValues = {
  BASE_CONFIG_LOADED: 10,
  AUTHENTICATING: 20,
  MASTER_DATA_LOADING: 40,
  MASTER_DATA_LOADED: 60,
  LOADING_CUSTOMERS: 70,
  LOADING_OPPORTUNITIES: 85,
  ALL_DATA_LOADED: 100
}

export const progressMessage = {
  LOADING_CONFIG_MSG: 'loading configuration',
  AUTHENTICATING_MSG: 'authenticating',
  MASTER_DATA_LOADING_MSG: 'loading master data',
  CUSTOMERS_LOADING_MSG: 'loading customers',
  OPPORTUNITIES_LOADING_MSG: 'loading opportunities'
}

export enum ProgressVariant {
  LOADING_BASE_CONFIG = 'loadingBaseConfig',
  AUTHENTICATING = 'authenticating',
  MASTER_DATA_LOADING = 'masterDataLoading',
  LOADING_CUSTOMERS = 'loadingCustomers',
  LOADING_OPPORTUNITIES = 'loadingOpportunities',
  ALL_DATA_LOADED = 'allDataLoaded'
}

export enum ProgressStatus {
  LOADING = 'loading',
  FAILURE = 'failure',
  SUCCESS = 'success'
}

export enum BadgeType {
  STATUS = 'status',
  CATEGORY = 'category'
}

export enum BadgeVariant {
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  NORMAL = 'normal',
  UNKNOWN = 'unknown'
}

export enum NotificationType {
  INFO = 'info',
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  MODAL = 'modal'
}

type ProgressMapValue = {
  message: string
  value: number
}

export const progressMap = new Map<ProgressVariant, ProgressMapValue>([
  [ProgressVariant.LOADING_BASE_CONFIG, { message: 'loading configuration', value: 10 }],
  [ProgressVariant.AUTHENTICATING, { message: 'authenticating', value: 20 }],
  [ProgressVariant.MASTER_DATA_LOADING, { message: 'loading master data', value: 40 }],
  [ProgressVariant.LOADING_CUSTOMERS, { message: 'loading customers', value: 60 }],
  [ProgressVariant.LOADING_OPPORTUNITIES, { message: 'loading opportunities', value: 80 }],
  [ProgressVariant.ALL_DATA_LOADED, { message: 'all data loaded', value: 100 }]
])

export const enum EnvironmentEnum {
  DEVELOPMENT = 'development',
  TEST_LOCAL = 'test-local',
  QA_QA = 'qa-qa',
  DEMO_QA = 'demo-qa',
  QA_MQA = 'qa-mqa',
  DEMO_MQA = 'demo-mqa',
  QA_QA1 = 'qa-qa1',
  DEMO_QA1 = 'demo-qa1',
  QA_QA2 = 'qa-qa2',
  DEMO_QA2 = 'demo-qa2',
  QA_QA2_V2 = 'qa-qa2-v2',
  DEMO_QA2_V2 = 'demo-qa2-v2',
  PRE_EU = 'pre-eu',
  PROD_EU = 'prod-eu'
}

export const environmentUrlMap = new Map<Environment, string>([
  [EnvironmentEnum.DEVELOPMENT, 'http://localhost:8080'],
  [EnvironmentEnum.QA_QA, 'https://qa-konecarecontract.kone.com'],
  [EnvironmentEnum.DEMO_QA, 'https://demo-konecarecontract.kone.com'],
  [EnvironmentEnum.QA_MQA, 'https://mqa-konecarecontract.kone.com'],
  [EnvironmentEnum.DEMO_MQA, 'https://mdemo-konecarecontract.kone.com'],
  [EnvironmentEnum.QA_QA1, 'https://qa1-konecarecontract.kone.com'],
  [EnvironmentEnum.DEMO_QA1, 'https://demo1-konecarecontract.kone.com'],
  [EnvironmentEnum.QA_QA2, 'https://qa2-konecarecontract.kone.com'],
  [EnvironmentEnum.DEMO_QA2, 'https://demo2-konecarecontract.kone.com'],
  [EnvironmentEnum.QA_QA2_V2, 'https://qa2-konecarecontract-v2.kone.com'],
  [EnvironmentEnum.DEMO_QA2_V2, 'https://demo2-konecarecontract-v2.kone.com'],
  [EnvironmentEnum.PRE_EU, 'https://pre-konecarecontract2.kone.com'],
  [EnvironmentEnum.PROD_EU, 'https://konecarecontract2.kone.com']
])

export enum SessionStatus {
  LOADING = 'loading',
  AUTHENTICATED = 'authenticated',
  UNAUTHENTICATED = 'unauthenticated'
}

export enum StateStatus {
  IDLE = 'idle',
  LOADING = 'loading',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed'
}

export const Icons = {
  location: 'icon-office-building-1',
  elevator: 'icon-elevator-1',
  escalator: 'icon-escalator',
  unknown: 'icon-close-circle-filled'
}

export const prepareEquipmentTabList = {
  location: 'location',
  elevator: 'elevator',
  escalator: 'escalator'
}

export const INVALID_BROWSER =
  'Invalid browser: Please note that only the latest Google Chrome, Ms Edge, Firefox and Safari is supported.'

export const INVALID_BROWSER_WITH_INFO = (browserDetails: Record<'name' | 'version', string>) =>
  `${INVALID_BROWSER}: ${browserDetails.name} ${browserDetails.version}`

export const tabIgnoreList = [
  'location',
  'elevator',
  'escalator',
  'contract',
  'contact',
  'account',
  'view-equipments',
  'edit',
  'overview'
] // Tabs to be hidden on these pages

export const MAIN_TAB_LIST: Record<string, NavigationTab> = {
  prepare: 'prepare',
  grouping: 'grouping',
  offers: 'offers',
  contract: 'contract'
}

export const innerTitleList = [
  // Titles for inner pages in the main offer flow
  { labelkey: 'navigation.refresh.location', value: 'location' },
  { labelkey: 'navigation.refresh.elevator', value: 'elevator' },
  { labelkey: 'navigation.refresh.escalator', value: 'escalator' },
  { labelkey: 'navigation.refresh.contract', value: 'contract' },
  { labelkey: 'navigation.refresh.contact', value: 'contact' },
  { labelkey: 'navigation.refresh.account', value: 'account' }
]

export const excelFixedKeys: { building: string[]; elevator: string[]; escalator: string[]; door: string[] } = {
  building: ['buildingDescription', 'buildingAddress', 'buildingCity'],

  elevator: [
    'equipmentAddress',
    'apfComponent',
    'koneConnectivityDeviceType',
    'inventoryNumber',
    'manufacturingSerialnumber',
    'manufacturer',
    'technicalPlatform',
    'equipmentType',
    'year',
    'doorsCount',
    'ratedSpeed',
    'ratedLoad',
    'additionalDoors',
    'startsPerYear',
    'operationalEnvironment'
  ],
  escalator: [
    'equipmentAddress',
    'inventoryNumber',
    'manufacturingSerialnumber',
    'manufacturer',
    'equipmentType',
    'escalatorType',
    'year',
    'rise',
    'length',
    'operationMode',
    'numberOfDrives',
    'operationalEnvironment',
    'operationalEnvironmentCondition'
  ],
  door: [
    'equipmentAddress',
    'inventoryNumber',
    'manufacturingSerialnumber',
    'manufacturer',
    'doorType',
    'year',
    'workingHeight',
    'doorUsage'
  ]
}

export const SIZES: Record<sizes, sizes> = {
  xsmall: 'xsmall',
  small: 'small',
  medium: 'medium',
  large: 'large'
}

export const elevatorTypes = ['elevator', 'dumbwaiter', 'stairlift', 'other']

export const escalatorTypes = ['escalator', 'autowalk']

export const operationalEnvironmentOptions = ['tough', 'normal', 'extra_tough']

export const PRICING_IMPACT_TERMS = ['KONDA', 'PROPORTIONAL_PRICE', 'VABNDAT', 'VBEGDAT']

export const DATE_FORMAT = 'DD/MM/YYYY'

export const ISO_DATE_FORMAT = 'YYYY-MM-DD'

export const COMMERCIAL_TERMS_TO_FILTER = ['KCSM_BILLING_MODE', 'PROPORTIONAL_PRICE', 'KONDA']

// Service configuration keys that should be rendered as checkbox inputs
export const CHECKBOX_SERVICE_KEYS = ['KCSM_NUMBER_JANITOR_VISIT']

export const PRICING_PROFILE = {
  KCSM_CARE_MBM: 'KCSM_CARE_MBM',
  numberOfDoors: 'numberOfDoors',
  usage: 'usage',
  speed: 'speed',
  load: 'load',
  technicalPlatform: 'technicalPlatform',
  carDoorType: 'carDoorType',
  age: 'age',
  equipmentName: 'equipmentName',
  segment: 'segment',
  fundingSector: 'fundingSector',
  equipmentCriticality: 'equipmentCriticality',
  opportunityCategory: 'opportunityCategory',
  operationalEnvironment: 'operationalEnvironment',
  escalatorOperationalEnvironment: 'escalatorOperationalEnvironment',
  numberOfElevators: 'number_of_equipment_elevators',
  numberOfEscalators: 'number_of_equipment_escalators'
}

export const EDIT_OFFER_TABS = {
  serviceConfiguration: 'serviceConfiguration',
  commercialTerms: 'commercialTerms',
  offerInstruction: 'offerInstruction'
}

export const FALLBACK_TRANSLATION = {
  ok: 'Ok',
  login: 'Login',
  duplicateloginTitle: 'Another login detected',
  duplicateloginMessage:
    "Your account is logged in from another device or browser. If this wasn't you, please secure your account.",
  sessionExpiredTitle: 'Session Expired',
  sessionExpiredMessage: 'Your session has expired. Please log in again.'
}

export const Translations = {
  // Dialog Translations
  confirmTitle: 'dialog.confirmTitle',
  lockedSapOffer: 'offerWizard.summary.lockedSAPOffer',
  sfreviewWait: 'review.salesforcePleaseWait',
  offerFinalizeDocDownloadWait: 'offerWizard.finalizeOffer.retrievingDocumentPleaseWait',
  offerFinalizeDocRecreateWait: 'offerWizard.finalizeOffer.documentsRecreatingPleaseWait',
  offerFinalizeGendocRetrievingWait: 'offerWizard.finalizeOffer.retrievingDocumentPleaseWait',
  offerWizardConfirmChange: 'offerWizard.summary.confirmChange',
  changingVersionWait: 'offerWizard.summary.changingVersion',
  sendingEmailWait: 'offerWizard.finalizeOffer.sendingEmailPleaseWait'
}

export const TENDER_STATUS: Record<string, OFFER_STATUS> = {
  draft: 'DRAFT',
  finalizing: 'FINALIZING',
  finalized: 'FINALIZED',
  finalizedActive: 'FINALIZED_ACTIVE',
  finalizedRefresh: 'FINALIZED_REFRESH',
  finalizedApprovalPending: 'FINALIZED_APPROVAL_PENDING',
  finalizeError: 'FINALIZE_ERROR',
  sentToSap: 'SENT_TO_SAP',
  unknown: 'UNKNOWN'
}

export const EQUIPMENT_CREATION_STATUS = {
  COMPLETE: 'complete',
  PARTIAL: 'partial',
  FAILED: 'failed'
} as const

export type EquipmentCreationStatus = (typeof EQUIPMENT_CREATION_STATUS)[keyof typeof EQUIPMENT_CREATION_STATUS]

export const CUSTOM_FIELD_VALUE = 'custom_field'
export const apfComponentField = 'apfComponent'

export const PRICING_PERIOD = {
  YEARLY: 'yearly',
  MONTHLY: 'monthly'
}

export const tableHeaderJustifyStylingList = ['priceString']
